import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { listMySuppliers, type MySuppliersPageDto } from '../api/mySuppliers'
import { useAuth } from '../auth/AuthProvider'
import { standardPlanUiCopy, startPlanUiCopy } from '../billing/startPlanUiCopy'
import { useBuyerEntitlements } from '../hooks/useBuyerEntitlements'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { DataTable } from '../components/DataTable'
import { formatRfqDate } from './rfqStatus'

export const SuppliersScreen = () => {
  const { session } = useAuth()
  const entitlements = useBuyerEntitlements()
  const uiCopy = entitlements.isStartPlan ? startPlanUiCopy : standardPlanUiCopy
  const navigate = useNavigate()
  const [pageIndex, setPageIndex] = useState(0)
  const [page, setPage] = useState<MySuppliersPageDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPage = useCallback(
    async (index: number) => {
      if (!session) {
        return
      }
      setLoading(true)
      setError(null)
      try {
        const result = await listMySuppliers(session.organizationId, index)
        setPage(result)
        setPageIndex(index)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить поставщиков')
      } finally {
        setLoading(false)
      }
    },
    [session],
  )

  useEffect(() => {
    void loadPage(0)
  }, [loadPage])

  return (
    <section className="market-page">
      <MarketPageHeader
        title="Мои поставщики"
        description="Поставщики, с которыми вы уже работали через Провизиум."
      />

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && page?.items.length === 0 ? (
        <MarketEmptyState
          title="Поставщиков пока нет"
          description={uiCopy.suppliersEmptyDescription}
          actions={[
            {
              label: uiCopy.suppliersEmptyActionLabel,
              to: entitlements.isStartPlan ? '/my-prices' : '/catalog',
            },
            { label: 'Корзина', to: '/cart', variant: 'ghost' },
          ]}
        />
      ) : null}

      {page && page.items.length > 0 ? (
        <DataTable wrapClassName="market-table-wrap--cards" className="market-table--cards" caption="Мои поставщики">
            <thead>
              <tr>
                <th>Поставщик</th>
                <th>Email</th>
                <th>Заказов</th>
                <th>Последний заказ</th>
                <th aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {page.items.map((supplier) => (
                <tr key={supplier.supplierOrganizationId}>
                  <td data-label="Поставщик" className="market-table__cell--truncate market-table__cell--primary">
                    {supplier.supplierName}
                  </td>
                  <td data-label="Email" className="market-table__cell--truncate">{supplier.email ?? '—'}</td>
                  <td data-label="Заказов">{supplier.ordersCount}</td>
                  <td data-label="Последний заказ">
                    {supplier.lastOrderedAt ? formatRfqDate(supplier.lastOrderedAt) : '—'}
                  </td>
                  <td className="market-table__cell--action">
                    <button
                      type="button"
                      className="echat-btn echat-btn--ghost echat-btn--sm market-table__row-action"
                      onClick={() => navigate('/catalog')}
                    >
                      Каталог
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
        </DataTable>
      ) : null}

      {page && page.totalPages > 1 ? (
        <div className="market-pagination">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex <= 0}
            onClick={() => void loadPage(pageIndex - 1)}
          >
            ←
          </button>
          <span>
            Страница {page.page + 1} из {page.totalPages}
          </span>
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex >= page.totalPages - 1}
            onClick={() => void loadPage(pageIndex + 1)}
          >
            →
          </button>
        </div>
      ) : null}
    </section>
  )
}
